/**
 * Threat levels — the single severity ladder the whole app keys off.
 *
 * This is a core seam for the Milestone-2 *activation-criteria engine*: that engine
 * will ingest signals (NWS alerts, SPC risk, tropical proximity, model fields off the
 * R2 feeds) and emit a `ThreatLevel` + reasons. For Milestone 1 we derive the level
 * from NWS alerts + SPC only (see deriveLevelFromAlerts), but everything downstream
 * — the Home status banner, the ambient glow, the (future) Storm Mode trigger — already
 * speaks in these terms, so swapping in the real engine is a drop-in.
 */
export type ThreatLevel = 'quiet' | 'info' | 'watch' | 'warning' | 'severe' | 'extreme'

export const LEVEL_ORDER: ThreatLevel[] = [
  'quiet',
  'info',
  'watch',
  'warning',
  'severe',
  'extreme',
]

export function levelRank(level: ThreatLevel): number {
  return LEVEL_ORDER.indexOf(level)
}

/** Highest (most severe) of two levels. */
export function maxLevel(a: ThreatLevel, b: ThreatLevel): ThreatLevel {
  return levelRank(a) >= levelRank(b) ? a : b
}

export function highestLevel(levels: ThreatLevel[]): ThreatLevel {
  return levels.reduce<ThreatLevel>((acc, l) => maxLevel(acc, l), 'quiet')
}

const COLOR: Record<ThreatLevel, string> = {
  quiet: 'var(--status-quiet)',
  info: 'var(--status-info)',
  watch: 'var(--status-watch)',
  warning: 'var(--status-warn)',
  severe: 'var(--status-severe)',
  extreme: 'var(--status-extreme)',
}

const DIM: Record<ThreatLevel, string> = {
  quiet: 'var(--status-quiet-dim)',
  info: 'var(--status-info-dim)',
  watch: 'var(--status-watch-dim)',
  warning: 'var(--status-warn-dim)',
  severe: 'var(--status-severe-dim)',
  extreme: 'var(--status-severe-dim)',
}

// Raw rgba for the body ambient glow (can't be a CSS var indirection here).
const GLOW: Record<ThreatLevel, string> = {
  quiet: 'rgba(52, 211, 153, 0.10)',
  info: 'rgba(70, 200, 232, 0.12)',
  watch: 'rgba(251, 191, 36, 0.14)',
  warning: 'rgba(251, 146, 60, 0.16)',
  severe: 'rgba(248, 113, 113, 0.18)',
  extreme: 'rgba(239, 68, 68, 0.22)',
}

const LABEL: Record<ThreatLevel, string> = {
  quiet: 'All Clear',
  info: 'Quiet',
  watch: 'Watch',
  warning: 'Warning',
  severe: 'Severe',
  extreme: 'Emergency',
}

export const threatColor = (l: ThreatLevel) => COLOR[l]
export const threatDim = (l: ThreatLevel) => DIM[l]
export const threatGlow = (l: ThreatLevel) => GLOW[l]
export const threatLabel = (l: ThreatLevel) => LABEL[l]

/** Is this level calm enough to show the serene "all-clear" home? */
export const isCalm = (l: ThreatLevel) => levelRank(l) <= levelRank('info')

/**
 * Motion urgency for the "living home" — animations tighten as weather escalates.
 * Drives the `--urg` custom property on animated glyphs (1 = calm, ~1.85 = severe).
 * This is the MOTION layer; the color still rides the radar ramp via threatColor().
 */
const URGENCY: Record<ThreatLevel, number> = {
  quiet: 1,
  info: 1,
  watch: 1.2,
  warning: 1.45,
  severe: 1.65,
  extreme: 1.85,
}
export const urgencyForLevel = (l: ThreatLevel) => URGENCY[l]
